/**
 * Página PÚBLICA de profesor (F3-D) — guest-first. Presentacional
 * (`PublicTeacherContent`, reutilizada por la vista pública y por la vista
 * previa del editor) + contenedora que la descarga por REST
 * (`GET /api/public/teachers/:orgSlug/:slug`) con estados de carga / no
 * encontrado (patrón entity-resolution). SEO via document title/meta.
 */
import { type Component, createEffect, createMemo, For, onCleanup, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { msg } from "@lingui/core/macro";
import { GraduationCap } from "lucide-solid";
import {
  academyPeopleQueryKeys,
  fetchPublicTeacherPage,
  type PublicTeacherPageDTO,
  specialtiesToChips,
} from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { SERVER_URL } from "@/lib/runtime-config";
import { AppBackground } from "@/components/app-background";
import { GlassSurface } from "@/components/ui/glass-surface";
import { Skeleton } from "@/components/ui/skeleton";
import { NotFoundState } from "@/components/ui/not-found-state";

/** Presentacional: pinta la página a partir del DTO (público o preview). */
export const PublicTeacherContent: Component<{
  page: PublicTeacherPageDTO;
  /** Base para los enlaces de curso (público real vs preview inerte). */
  preview?: boolean;
}> = (props) => {
  const { t } = useI18n();
  const chips = createMemo(() => specialtiesToChips(props.page.teacher.specialties));

  return (
    <div class="mx-auto max-w-3xl space-y-6" data-testid="public-teacher">
      {/* Hero */}
      <GlassSurface class="flex flex-col items-center gap-4 p-8 text-center">
        <Show
          when={props.page.teacher.photo}
          fallback={
            <div class="flex h-24 w-24 items-center justify-center rounded-full bg-accent/15 text-2xl font-bold text-accent">
              {(props.page.teacher.name ?? "?").slice(0, 2).toUpperCase()}
            </div>
          }
        >
          {(photo) => (
            <img
              src={photo()}
              alt={props.page.teacher.name ?? ""}
              class="h-24 w-24 rounded-full object-cover shadow-lg"
            />
          )}
        </Show>
        <div>
          <h1 class="font-display text-3xl font-extrabold" data-testid="public-teacher-name">
            {props.page.teacher.name ?? t(msg`Teacher`)}
          </h1>
          <Show when={props.page.teacher.title}>
            {(title) => <p class="text-sm font-semibold text-primary">{title()}</p>}
          </Show>
          <Show when={props.page.org.name}>
            {(org) => <p class="mt-1 text-xs text-muted-foreground">{org()}</p>}
          </Show>
        </div>
        <Show when={chips().length > 0}>
          <div class="flex flex-wrap justify-center gap-2">
            <For each={chips()}>
              {(chip) => (
                <span class="rounded-full bg-accent/12 px-3 py-1 text-xs font-bold text-accent">
                  {chip}
                </span>
              )}
            </For>
          </div>
        </Show>
      </GlassSurface>

      {/* Bio */}
      <Show when={props.page.teacher.bio}>
        {(bio) => (
          <GlassSurface class="p-6">
            <p class="whitespace-pre-line text-sm leading-relaxed text-foreground/90">{bio()}</p>
          </GlassSurface>
        )}
      </Show>

      {/* Cursos públicos */}
      <Show when={props.page.courses.length > 0}>
        <div class="space-y-3">
          <h2 class="font-display text-lg font-bold">{t(msg`Courses`)}</h2>
          <div class="grid gap-3 sm:grid-cols-2" data-testid="public-teacher-courses">
            <For each={props.page.courses}>
              {(course) => (
                <a
                  href={props.preview ? undefined : `/catalog/${course.slug ?? course.id}`}
                  class="block rounded-2xl border border-border p-4 transition-colors hover:border-primary/50"
                  data-testid="public-course-card"
                >
                  <Show when={course.coverImage}>
                    {(cover) => (
                      <img src={cover()} alt="" class="mb-3 h-28 w-full rounded-xl object-cover" />
                    )}
                  </Show>
                  <div class="flex items-center gap-2">
                    <GraduationCap class="h-4 w-4 text-primary" />
                    <p class="font-semibold">{course.title}</p>
                  </div>
                  <Show when={course.description}>
                    {(desc) => (
                      <p class="mt-1 line-clamp-2 text-xs text-muted-foreground">{desc()}</p>
                    )}
                  </Show>
                </a>
              )}
            </For>
          </div>
        </div>
      </Show>
    </div>
  );
};

/** Contenedora pública: descarga por REST con estados de carga/404. */
export const PublicTeacherPageView: Component<{ orgSlug: string; slug: string }> = (props) => {
  const { t } = useI18n();

  const query = createQuery<PublicTeacherPageDTO>(() => ({
    queryKey: academyPeopleQueryKeys.publicTeacher(props.orgSlug, props.slug),
    queryFn: ({ signal }) =>
      fetchPublicTeacherPage(SERVER_URL, props.orgSlug, props.slug, { signal }),
    staleTime: 60_000,
    // 404 (perfil inexistente/privado) o error de red → NotFoundState. Un solo
    // reintento como en la verificación de certificados.
    retry: 1,
  }));

  // SEO: título + meta description desde el perfil.
  createEffect(() => {
    const page = query.data;
    if (!page) {
      return;
    }
    const prevTitle = document.title;
    const name = page.teacher.name ?? "";
    const org = page.org.name ?? "";
    document.title = `${name}${org ? ` · ${org}` : ""}`;
    const meta = ensureMeta();
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute("content", page.teacher.title ?? page.teacher.bio?.slice(0, 150) ?? name);
    onCleanup(() => {
      document.title = prevTitle;
      if (prevDesc !== null) {
        meta.setAttribute("content", prevDesc);
      }
    });
  });

  return (
    <div class="relative min-h-dvh">
      <AppBackground />
      <div class="mx-auto max-w-3xl p-4 py-10 md:p-8">
        <Show when={!query.isLoading} fallback={<Skeleton class="h-96 w-full rounded-3xl" />}>
          <Show
            when={query.data}
            fallback={
              <NotFoundState
                variant="not-found"
                title={t(msg`Teacher not found`)}
                description={t(msg`This public profile doesn't exist or isn't published.`)}
              />
            }
          >
            {(page) => <PublicTeacherContent page={page()} />}
          </Show>
        </Show>
      </div>
    </div>
  );
};

/** Obtiene (o crea) la etiqueta `<meta name="description">`. */
function ensureMeta(): HTMLMetaElement {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.append(meta);
  }
  return meta;
}
